/**
 * Search over the tag-icon picker's names, in every language at once.
 *
 * Each icon has 2–8 names per locale (`packages/styles/icons/keywords/<locale>.json`,
 * the first being its display name). A query is matched against the names of
 * ALL locales, so someone running the app in English who types 星 or "gato"
 * still finds the star or the cat. Mirrored 1:1 in Swift by `EspurnaIconSearch`
 * (CaliuIcons); both run the shared fixtures in `iconSearch.fixtures.json`.
 * Change both or neither.
 */

/** `index[key][locale]` = the icon's names in that locale, display name first. */
export type IconKeywordIndex = Record<string, Partial<Record<string, readonly string[]>>>

/** How a name matched, best first. */
const EXACT = 0
const WORD = 1
const PREFIX = 2
const WORD_PREFIX = 3
const SUBSTRING = 4

/**
 * Folds the differences people don't type: width (NFKC), case, Latin/Greek/
 * Cyrillic accents (é → e, ё → е), katakana → hiragana, and the punctuation
 * inside words (l·l, apostrophes, hyphens). Kanji are left alone, which is why
 * the Japanese names list readings next to kanji.
 */
export function normalizeIconSearchText(text: string): string {
  let out = text.normalize("NFKC").toLowerCase()
  out = out.normalize("NFD").replace(/[\u0300-\u036f]/g, "").normalize("NFC")
  out = out.replace(/[\u30a1-\u30f6]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60))
  out = out.replace(/[·'’ʼ]/g, "").replace(/[-_/.,]/g, " ")
  return out.replace(/\s+/g, " ").trim()
}

/**
 * Alphabetic scripts match whole words or word starts only: "gato" inside
 * "obligatori" is noise. Scripts written without spaces (CJK, kana, Hangul)
 * have no word starts to anchor on, so they also match anywhere in a name.
 */
function allowsSubstring(token: string): boolean {
  for (const ch of token) {
    if (ch.codePointAt(0)! >= 0x0530) return true
  }
  return false
}

function matchTier(name: string, query: string): number | null {
  if (name === query) return EXACT
  const words = name.split(" ")
  const tokens = query.split(" ")
  if (tokens.length === 1 && words.includes(query)) return WORD
  if (name.startsWith(query)) return PREFIX
  let worst = EXACT
  for (const token of tokens) {
    let tier: number | null = null
    if (words.includes(token)) tier = WORD
    else if (words.some((word) => word.startsWith(token))) tier = WORD_PREFIX
    else if (allowsSubstring(token) && name.includes(token)) tier = SUBSTRING
    if (tier === null) return null
    worst = Math.max(worst, tier)
  }
  return worst
}

/**
 * Icon keys whose names match `query`. Matches in the user's own `locale` come
 * first, then matches in any other language; each group is ordered exact name,
 * whole word, name prefix, word prefix, substring, then index order (the
 * picker's display order). An empty query returns nothing; the picker shows its
 * sections instead.
 */
export function searchIcons(query: string, index: IconKeywordIndex, locale: string): string[] {
  const q = normalizeIconSearchText(query)
  if (!q) return []
  const hits: { key: string; tier: number; foreign: number; order: number }[] = []
  let order = 0
  for (const [key, byLocale] of Object.entries(index)) {
    let best: { tier: number; foreign: number } | null = null
    for (const [nameLocale, names] of Object.entries(byLocale)) {
      const foreign = nameLocale === locale ? 0 : 1
      for (const name of names ?? []) {
        const tier = matchTier(normalizeIconSearchText(name), q)
        if (tier === null) continue
        if (!best || foreign < best.foreign || (foreign === best.foreign && tier < best.tier)) {
          best = { tier, foreign }
        }
      }
    }
    if (best) hits.push({ key, ...best, order })
    order += 1
  }
  hits.sort((a, b) => a.foreign - b.foreign || a.tier - b.tier || a.order - b.order)
  return hits.map((hit) => hit.key)
}
